import { Checkbox, ListItemText, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDropDown } from '@material-ui/icons';
import { useState } from 'react';
import { CheckboxMenu, CheckboxMenuOption } from './checkboxMenu';
import { usePillSelectStyles } from './pillSelect';

const useStyles = makeStyles(() => ({
  button: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 260,
  },
  valueText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface PillMultiSelectProps {
  label: string;
  allLabel: string;
  value: string[];
  options: CheckboxMenuOption[];
  onChange: (value: string[]) => void;
}

// The choice is applied when the menu closes, so ticking several options loads the data once.
// The options can be searched by name or email, as there can be many senders.
export const PillMultiSelect = ({
  label,
  allLabel,
  value,
  options,
  onChange,
}: PillMultiSelectProps) => {
  const pillClasses = usePillSelectStyles();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[] | null>(null);
  const selected = draft ?? value;
  const labelOf = (selectedValue: string) =>
    options.find(option => option.value === selectedValue)?.label ??
    selectedValue;

  const toggle = (option: string) =>
    setDraft(
      selected.includes(option)
        ? selected.filter(v => v !== option)
        : [...selected, option]
    );

  const handleClose = () => {
    if (
      draft &&
      (draft.length !== value.length || draft.some(v => !value.includes(v)))
    ) {
      onChange(draft);
    }
    setDraft(null);
    setAnchorEl(null);
  };

  const shownValue =
    selected.length === 0
      ? allLabel
      : selected.length === 1
      ? labelOf(selected[0])
      : `${labelOf(selected[0])} +${selected.length - 1}`;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={`${pillClasses.root} ${classes.button}`}
        onClick={e => setAnchorEl(e.currentTarget)}
        onKeyDown={e =>
          (e.key === 'Enter' || e.key === ' ') && setAnchorEl(e.currentTarget)
        }
      >
        <span className={pillClasses.label}>{label}</span>
        <span className={`${pillClasses.select} ${classes.value}`}>
          <span className={classes.valueText}>{shownValue}</span>
          <ArrowDropDown fontSize="small" />
        </span>
      </div>
      <CheckboxMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        options={options}
        selected={selected}
        onToggle={toggle}
        searchable
        firstItem={
          <MenuItem onClick={() => setDraft([])}>
            <Checkbox checked={selected.length === 0} />
            <ListItemText primary={allLabel} />
          </MenuItem>
        }
      />
    </>
  );
};
